import React, { useCallback, useRef, useEffect, useState, } from 'react'
import { base64StringToBlob, } from 'blob-util'
import { saveAs, } from 'file-saver'
import { format, } from 'date-fns'
import { Subject, EMPTY, } from 'rxjs'
import { map, switchMap, catchError, } from 'rxjs/operators'
import { AsyncState, } from '@shapr/react-shared/types/Async'
import { getResponseData, } from '~/utils/api'

import {
  getJobTitleSuggestions as getJobTitleSuggestionsRequest,
  getLocationSuggestions as getLocationSuggestionsRequest,
  getInterestsSuggestions as getInterestsSuggestionsRequest,
  getSearchResult as getSearchResultRequest,
} from './services/searchServices'
import useCriteria from './hooks/useCriteria'
import Search from './Search'

const SearchContainer = props => {
  const handleCriteriaMultipleSelectionChange = useCallback(
    ({ criteria, type, value, }) => {
      const computedValue = value || criteria.inputValue
      if (type === 'add') {
        criteria.setSelectedValues(selectedValues => [
          ...new Set([
            ...selectedValues,
            ...computedValue
              .split(';')
              .map(e => e.trim())
              .filter(e => e),
          ]),
        ])
        if (!value) {
          criteria.setInputValue('')
          if (criteria.isWithSuggestions) criteria.setSuggestionIsOpen(false)
        }
      } else if (type === 'remove')
        criteria.setSelectedValues(selectedValues =>
          selectedValues.filter(e => e !== computedValue),
        )
    },
    [],
  )

  const firstCriteriaInputRef = useRef()
  useEffect(() => {
    if (firstCriteriaInputRef.current) firstCriteriaInputRef.current.focus()
  }, [])

  const createNewCriteria = useCallback(
    data =>
      useCriteria({
        onMultipleSelectionChange: handleCriteriaMultipleSelectionChange,
        ...data,
      }),
    [handleCriteriaMultipleSelectionChange,],
  )

  const jobTitleCriteria = createNewCriteria({
    label: 'Job Titles',
    inputRef: firstCriteriaInputRef,
    isWithMultipleSelection: true,
    isWithSuggestions: true,
    isWithAddButton: true,
  })

  const locationCriteria = createNewCriteria({
    label: 'Location',
    isWithMultipleSelection: true,
    isWithSuggestions: true,
    initialSuggestionValues: ['apple', 'arrow',],
  })

  const excludedCompaniesCriteria = createNewCriteria({
    label: 'Excluded terms',
    isWithMultipleSelection: true,
    isWithAddButton: true,
  })

  const includedCompaniesCriteria = createNewCriteria({
    label: 'Included companies',
    isWithMultipleSelection: true,
    isWithAddButton: true,
  })

  const GoalsCriteria = createNewCriteria({
    label: 'Goals',
    isWithMultipleSelection: true,
    isWithSuggestions: true,
    isWithAddButton: true,
  })

  const InterestsCriteria = createNewCriteria({
    label: 'Interests',
    isWithMultipleSelection: true,
    isWithSuggestions: true,
    initialSuggestionValues: ['business', 'developpement',],
  })

  const SchoolsCriteria = createNewCriteria({
    label: 'Schools',
    isWithMultipleSelection: true,
    isWithAddButton: true,
  })

  const MinYearsExperienceCriteria = createNewCriteria({
    label: 'Minimal Years of Experience',
    isWithMultipleSelection: false,
    isWithAddButton: false,
    initialInputValue: 0,
  })

  const MaxYearsExperienceCriteria = createNewCriteria({
    label: 'Maximal Years of Experience',
    isWithMultipleSelection: false,
    isWithAddButton: false,
    initialInputValue: 30,
  })

  const SegmentCriteria = createNewCriteria({
    label: 'Segment',
    isWithMultipleSelection: true,
    isWithSuggestions: true,
    initialSuggestionValues: ['2', '4', '6', '8',],
  })

  const criterias = [
    jobTitleCriteria,
    locationCriteria,
    excludedCompaniesCriteria,
    includedCompaniesCriteria,
    SchoolsCriteria,
    GoalsCriteria,
    InterestsCriteria,
    MinYearsExperienceCriteria,
    MaxYearsExperienceCriteria,
    SegmentCriteria,
  ]

  const getJobTitleSuggestions$ = useRef(new Subject())
  const getJobTitleSuggestions = useRef(undefined)

  useEffect(() => {
    getJobTitleSuggestions.current = getJobTitleSuggestions$.current
      .pipe(
        switchMap(inputValue => {
          jobTitleCriteria.setSuggestionValues([])
          if (inputValue.length < 3) return EMPTY
          else
            return getJobTitleSuggestionsRequest({ name: inputValue, }).pipe(
              map(response => {
                jobTitleCriteria.setSuggestionValues(getResponseData(response))
              }),
              catchError(() => {
                jobTitleCriteria.setSuggestionValues([])
                return EMPTY
              }),
            )
        }),
      )
      .subscribe()
    return () => {
      getJobTitleSuggestions.current.unsubscribe()
    }
  }, []) //eslint-disable-line

  useEffect(() => {
    getJobTitleSuggestions$.current.next(jobTitleCriteria.inputValue)
  }, [jobTitleCriteria.inputValue,])

  const getLocationSuggestions$ = useRef(new Subject())
  const getLocationSuggestions = useRef(undefined)
  useEffect(() => {
    getLocationSuggestions.current = getLocationSuggestions$.current
      .pipe(
        switchMap(inputValue => {
          locationCriteria.setSuggestionValues([])
          if (inputValue.length < 3) return EMPTY
          else
            return getLocationSuggestionsRequest({ name: inputValue, }).pipe(
              map(response => {
                locationCriteria.setSuggestionValues(getResponseData(response))
              }),
              catchError(() => {
                locationCriteria.setSuggestionValues([])
                return EMPTY
              }),
            )
        }),
      )
      .subscribe()
    return () => {
      getLocationSuggestions.current.unsubscribe()
    }
  }, []) //eslint-disable-line

  useEffect(() => {
    getLocationSuggestions$.current.next(locationCriteria.inputValue)
  }, [locationCriteria.inputValue,])

  const getInterestsSuggestions$ = useRef(new Subject())
  const getInterestsSuggestions = useRef(undefined)
  useEffect(() => {
    getInterestsSuggestions.current = getInterestsSuggestions$.current
      .pipe(
        switchMap(inputValue => {
          InterestsCriteria.setSuggestionValues([])
          if (inputValue.length < 3) return EMPTY
          else
            return getInterestsSuggestionsRequest({ name: inputValue, }).pipe(
              map(response => {
                InterestsCriteria.setSuggestionValues(getResponseData(response))
              }),
              catchError(() => {
                InterestsCriteria.setSuggestionValues([])
                return EMPTY
              }),
            )
        }),
      )
      .subscribe()
    return () => {
      getInterestsSuggestions.current.unsubscribe()
    }
  }, []) //eslint-disable-line

  useEffect(() => {
    getInterestsSuggestions$.current.next(InterestsCriteria.inputValue)
  }, [InterestsCriteria.inputValue,])

  useEffect(() => {
    GoalsCriteria.setSuggestionValues([
      'Funding',
      'Collaborators & talent to hire',
      'Potential investments',
      'Job opportunities',
      'A co-founder',
      'Ideas & inspiration',
      'Mentorship',
      'Freelance projects',
      'New friends in town',
      'Mentor others',
      'Grow my business',
      'Explore a career change',
      'Hire freelancers',
    ])
  }, [GoalsCriteria.inputValue]) //eslint-disable-line

  const [getSearchResultState, setGetSearchResultState,] = useState(
    AsyncState.initial,
  )
  const [result, setResult,] = useState(undefined)
  const [resultNumber, setResultNumber,] = useState(undefined)

  const getSearchResult$ = useRef(new Subject())
  const getSearchResult = useRef(undefined)
  useEffect(() => {
    getSearchResult.current = getSearchResult$.current
      .pipe(
        switchMap(
          ({
            jobTitles,
            locations,
            includedCompanies,
            excludedCompanies,
            goals,
            interests,
            schools,
            experience,
            segment,
          }) => {
            setGetSearchResultState(AsyncState.pending)
            return getSearchResultRequest({
              jobTitles,
              locations,
              includedCompanies,
              excludedCompanies,
              goals,
              interests,
              schools,
              experience,
              segment,
            }).pipe(
              map(response => {
                setResult(response)
                if (response.data.results)
                  setResultNumber(response.data.results)
                else setResultNumber(0)
                setGetSearchResultState(AsyncState.success)
              }),
              catchError(() => {
                setGetSearchResultState(AsyncState.fail)
                return EMPTY
              }),
            )
          },
        ),
      )
      .subscribe()
    return () => {
      getSearchResult.current.unsubscribe()
    }
  }, [])

  const onDownload = useCallback(() => {
    const resultBlob = base64StringToBlob(getResponseData(result).base64)
    saveAs(
      resultBlob,
      `${format(new Date(), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx")}.csv`,
    )
  }, [result,])

  const onSearch = useCallback(() => {
    getSearchResult$.current.next({
      jobTitles: jobTitleCriteria.selectedValues,
      locations: locationCriteria.selectedValues,
      includedCompanies: includedCompaniesCriteria.selectedValues,
      excludedCompanies: excludedCompaniesCriteria.selectedValues,
      goals: GoalsCriteria.selectedValues,
      interests: InterestsCriteria.selectedValues,
      schools: SchoolsCriteria.selectedValues,
      experience: [
        parseInt(MinYearsExperienceCriteria.inputValue, 10),
        parseInt(MaxYearsExperienceCriteria.inputValue, 10),
      ],
      segment: SegmentCriteria.selectedValues,
    })
  }, [
    jobTitleCriteria,
    locationCriteria,
    includedCompaniesCriteria,
    excludedCompaniesCriteria,
    SchoolsCriteria,
    GoalsCriteria,
    InterestsCriteria,
    MinYearsExperienceCriteria,
    MaxYearsExperienceCriteria,
    SegmentCriteria,
  ])

  useEffect(() => {
    if (
      jobTitleCriteria.selectedValues.length ||
      locationCriteria.selectedValues.length ||
      includedCompaniesCriteria.selectedValues.length ||
      excludedCompaniesCriteria.selectedValues.length ||
      SchoolsCriteria.selectedValues.length ||
      GoalsCriteria.selectedValues.length ||
      InterestsCriteria.selectedValues.length ||
      MinYearsExperienceCriteria.inputValue ||
      MaxYearsExperienceCriteria.inputValue ||
      SegmentCriteria.selectedValues.length
    )
      onSearch()
  }, [jobTitleCriteria.selectedValues, locationCriteria.selectedValues, includedCompaniesCriteria.selectedValues, excludedCompaniesCriteria.selectedValues, SchoolsCriteria.selectedValues, GoalsCriteria.selectedValues, InterestsCriteria.selectedValues, MinYearsExperienceCriteria.inputValue, MaxYearsExperienceCriteria.inputValue, SegmentCriteria.selectedValues, onSearch]) //eslint-disable-line

  const onReset = useCallback(() => {
    criterias.forEach(criteria => {
      const {
        setInputValue,
        setSuggestionValues,
        setSuggestionIsOpen,
        setSelectedValues,
      } = criteria
      setInputValue('')
      if (setSuggestionValues) setSuggestionValues([])
      if (setSuggestionIsOpen) setSuggestionIsOpen(false)
      if (setSelectedValues) setSelectedValues([])
    })
  }, [criterias,])

  return (
    <Search
      onDownload={onDownload}
      onReset={onReset}
      criterias={criterias}
      getSearchResultState={getSearchResultState}
      resultNumber={resultNumber}
      {...props}
    />
  )
}

export default SearchContainer
